import IconFileImport from "~icons/tabler/file-import";
import { usePlaybackMediator } from "@/contexts/playback-context";
import { useRef, useState } from "react";
import { RawDetection, RawSnapshot } from "@/shared/types";
import { toast } from "react-hot-toast";
import { useImportJson } from "@/playback-controls/hooks/useImportJson";
import { Button, Popover, Separator, Text } from "@radix-ui/themes";
import { useViewSettings } from "@/view-settings";

export function ImportButton() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const tracksInputRef = useRef<HTMLInputElement | null>(null);
  const detectionsInputRef = useRef<HTMLInputElement | null>(null);
  const mediator = usePlaybackMediator();
  const viewSettings = useViewSettings();

  const loadFile = useImportJson();

  const onImport = async () => {
    const trackFiles = tracksInputRef.current?.files;
    const detectionFiles = detectionsInputRef.current?.files;
    if (!trackFiles?.length && !detectionFiles?.length)
      throw Error("File is not found");

    setLoading(true);
    const promises: {
      tracks: Promise<RawSnapshot[] | null>;
      detections: Promise<RawDetection[] | null>;
    } = {
      tracks: Promise.resolve(null),
      detections: Promise.resolve(null),
    };

    if (trackFiles && trackFiles.length > 0)
      promises.tracks = loadFile(trackFiles[0]);
    if (detectionFiles && detectionFiles.length > 0)
      promises.detections = loadFile(detectionFiles[0]);

    try {
      const [tracks, detections] = await Promise.all([
        promises.tracks,
        promises.detections,
      ]);

      mediator.importJSON({
        tracks,
        detections,
      });
      setOpen(false);
      if (viewSettings.showVideo.peek()) {
        mediator.toggleVideo();
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger>
        <Button
          variant="soft"
          color="gray"
          size="3"
          radius="large"
          className="select-none text-sm leading-6"
        >
          <IconFileImport />
          Import
        </Button>
      </Popover.Trigger>
      <Popover.Content align="end" className="p-0 w-[300px]">
        <div className="p-4">
          <Text size="4" mb="4" as="p" className="font-semibold">
            Select a JSON file
          </Text>

          <Text
            as="label"
            size="2"
            weight="medium"
            htmlFor="track-snapshots-input"
          >
            Track snapshots
          </Text>
          <input
            id="track-snapshots-input"
            className="w-full mb-4 mt-1"
            ref={tracksInputRef}
            type="file"
            accept=".json"
            placeholder="Track snapshots"
          />

          <Text as="label" size="2" weight="medium" htmlFor="detections-input">
            Detections
          </Text>
          <input
            id="detections-input"
            className="w-full mt-1"
            ref={detectionsInputRef}
            type="file"
            accept=".json"
            placeholder="Detections"
          />
        </div>
        <Separator size="4" />
        <div className="p-3 flex justify-between">
          <Popover.Close>
            <Button variant="soft" color="gray" className="rounded-lg">
              Close
            </Button>
          </Popover.Close>
          <Button
            loading={loading}
            color="gray"
            highContrast
            className="rounded-lg"
            onClick={onImport}
          >
            Apply
          </Button>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}
